import React, { memo, useMemo } from 'react';
import {
  AbsoluteFill,
  getRemotionEnvironment,
  Sequence,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion';
import { linearTiming, TransitionSeries } from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import { slide, SlideDirection } from '@remotion/transitions/slide';
import { wipe, WipeDirection } from '@remotion/transitions/wipe';
import { flip, FlipDirection } from '@remotion/transitions/flip';
import { clockWipe } from '@remotion/transitions/clock-wipe';
import { iris } from '@remotion/transitions/iris';
import { TransitionGroup } from './utils/build-transition-groups';
import { FrozenFrameWrapper } from './frozen-frame-wrapper';
import { InnerLayer } from '../items/inner-layer';
import { isPrefetchEnabled, useLocalUrls } from '../caching/load-to-blob-url';
import {
  TransitionType,
  TransitionDirection,
  DEFAULT_TRANSITION_DIRECTION,
} from '../editor-property-panel/ui/contents/transition/transition-drag-data';
import {
  getPlaybackBoundaryPremountFramesForItem,
  getPlaybackBoundaryPremountFramesForSequenceStart,
  isPlaybackBoundaryMediaPauseDisabled,
} from './utils/playback-boundary-premount';

interface TransitionGroupLayerProps {
  group: TransitionGroup;
  trackMuted: boolean;
  zIndex: number;
  boundaryPremountFramesByItemId?: ReadonlyMap<string, number>;
}

/**
 * Transition 타입에 따라 적절한 TransitionSeries.Transition 요소를 렌더링합니다.
 * TransitionSeries는 직접적인 TransitionSeries.Transition 요소만 자식으로 허용하므로,
 * 각 transition 타입별로 별도의 요소를 반환해야 합니다.
 */
const renderTransitionElement = (
  key: string,
  transitionType: TransitionType,
  durationInFrames: number,
  width: number,
  height: number,
  direction?: TransitionDirection,
): React.ReactNode => {
  const timing = linearTiming({ durationInFrames });
  const dir = direction ?? DEFAULT_TRANSITION_DIRECTION;

  switch (transitionType) {
    case 'fade':
      return (
        <TransitionSeries.Transition
          key={key}
          presentation={fade()}
          timing={timing}
        />
      );
    case 'slide':
      return (
        <TransitionSeries.Transition
          key={key}
          presentation={slide({ direction: dir as SlideDirection })}
          timing={timing}
        />
      );
    case 'wipe':
      return (
        <TransitionSeries.Transition
          key={key}
          presentation={wipe({ direction: dir as WipeDirection })}
          timing={timing}
        />
      );
    case 'flip':
      return (
        <TransitionSeries.Transition
          key={key}
          presentation={flip({ direction: dir as FlipDirection })}
          timing={timing}
        />
      );
    case 'clockWipe':
      return (
        <TransitionSeries.Transition
          key={key}
          presentation={clockWipe({ width, height })}
          timing={timing}
        />
      );
    case 'iris':
      return (
        <TransitionSeries.Transition
          key={key}
          presentation={iris({ width, height })}
          timing={timing}
        />
      );
    default:
      return (
        <TransitionSeries.Transition
          key={key}
          presentation={fade()}
          timing={timing}
        />
      );
  }
};

/**
 * TransitionSeries를 사용하여 트랜지션으로 연결된 아이템 그룹을 렌더링합니다.
 *
 * @example
 * group.items = [A, B, C]
 * group.transitions = [trans1, trans2] (A→B, B→C)
 *
 * 렌더링 구조:
 * <Sequence from={group.startFrame}>
 *   <TransitionSeries>
 *     <TransitionSeries.Sequence>A</TransitionSeries.Sequence>
 *     <TransitionSeries.Transition trans1 />
 *     <TransitionSeries.Sequence>B</TransitionSeries.Sequence>
 *     <TransitionSeries.Transition trans2 />
 *     <TransitionSeries.Sequence>C</TransitionSeries.Sequence>
 *   </TransitionSeries>
 * </Sequence>
 */
// premount 상태에서 리소스가 로드되도록 display: block 설정
const styleWhilePremounted: React.CSSProperties = {
  display: 'block',
};

const TransitionGroupLayerUnmemoized: React.FC<TransitionGroupLayerProps> = ({
  group,
  trackMuted,
  zIndex,
  boundaryPremountFramesByItemId,
}) => {
  const { width, height, fps } = useVideoConfig();
  const currentFrame = useCurrentFrame();

  // Match the Player's plain-clip lookahead without disabling active buffering.
  const environment = getRemotionEnvironment();
  const isPlayerPreview = environment.isPlayer && !environment.isRendering;
  const reactiveLocalUrls = useLocalUrls();
  const defaultPremountFrames = useMemo(() => {
    if (isPlayerPreview || isPrefetchEnabled()) return Math.floor(3 * fps);
    const hasRemoteVideo = group.items.some((item) => {
      if (item.type !== 'video') return false;
      const assetId = (item as { assetId: string }).assetId;
      return !reactiveLocalUrls[assetId];
    });
    return hasRemoteVideo ? 0 : Math.floor(3 * fps);
  }, [fps, group.items, reactiveLocalUrls, isPlayerPreview]);

  const sequencePremountFrames = useMemo(
    () =>
      getPlaybackBoundaryPremountFramesForSequenceStart({
        items: group.items,
        startFrame: group.startFrame,
        defaultPremountFrames,
        boundaryPremountFramesByItemId,
      }),
    [
      boundaryPremountFramesByItemId,
      defaultPremountFrames,
      group.items,
      group.startFrame,
    ],
  );

  // 트랜지션 시리즈 요소들 메모이제이션
  const transitionElements = useMemo(() => {
    return group.items.flatMap((item, index) => {
      // 현재 아이템 뒤의 트랜지션 (source로서)
      const nextTransition = group.transitions[index];
      // 현재 아이템 앞의 트랜지션 (target으로서)
      const prevTransition = group.transitions[index - 1];

      // TransitionSeries 타이밍 보정:
      // UI에서 트랜지션은 두 클립 중앙에 배치됨 (source 끝 - duration/2)
      // TransitionSeries는 트랜지션을 첫 번째 시퀀스 끝에서 전체 duration만큼 겹침
      // 따라서 각 시퀀스에 인접 트랜지션의 절반(duration/2)을 추가해야 타이밍이 맞음
      //
      // 예시: sourceItem(0~100) - transition(30frames) - targetItem(100~200)
      // UI에서 트랜지션 위치: 85~115 (중앙 배치)
      // TransitionSeries에서:
      //   - seq1Duration = 100 + 15 = 115
      //   - 트랜지션 시작 = 115 - 30 = 85 ✓
      const nextTransHalf = nextTransition
        ? Math.floor(nextTransition.durationInFrames / 2)
        : 0;
      const prevTransHalf = prevTransition
        ? Math.ceil(prevTransition.durationInFrames / 2)
        : 0;

      // 확장 정보: 현재 아이템이 source로서 뒤로 확장된 프레임, target으로서 앞으로 확장된 프레임
      // 확장 정보는 effectiveDuration에 포함하지 않음 (트랜지션 시작 시간에 영향을 주지 않도록)
      // FrozenFrameWrapper에서 확장된 영상은 freeze 대신 재생됨
      const sourceExtension = nextTransition?.sourceExtendedFrames ?? 0;
      const targetExtension = prevTransition?.targetExtendedFrames ?? 0;
      const boundaryPremountFor =
        boundaryPremountFramesByItemId?.get(item.id) ?? 0;
      // Boundary overrides remain item-scoped. Player's ordinary lookahead
      // prepares remote originals without disabling their active buffering.
      const itemPremountFrames = getPlaybackBoundaryPremountFramesForItem({
        itemId: item.id,
        defaultPremountFrames,
        boundaryPremountFramesByItemId,
      });
      const disableBoundaryMediaPause = isPlaybackBoundaryMediaPauseDisabled({
        currentFrame,
        itemFrom: item.from,
        premountFrames: boundaryPremountFor,
      });

      const effectiveDuration =
        item.durationInFrames + nextTransHalf + prevTransHalf;

      const elements: React.ReactNode[] = [
        <TransitionSeries.Sequence
          key={`seq-${item.id}`}
          durationInFrames={effectiveDuration}
          premountFor={itemPremountFrames}
          styleWhilePremounted={styleWhilePremounted}
        >
          <AbsoluteFill>
            <FrozenFrameWrapper
              item={item}
              nextTransition={nextTransition}
              prevTransition={prevTransition}
              sourceExtension={sourceExtension}
              targetExtension={targetExtension}
            >
              <InnerLayer
                item={item}
                trackMuted={trackMuted}
                cropBackground={false}
                zIndex={zIndex}
                disableBoundaryMediaPause={disableBoundaryMediaPause}
              />
            </FrozenFrameWrapper>
          </AbsoluteFill>
        </TransitionSeries.Sequence>,
      ];

      if (nextTransition) {
        elements.push(
          renderTransitionElement(
            `trans-${item.id}`,
            nextTransition.transitionType,
            nextTransition.durationInFrames,
            width,
            height,
            nextTransition.direction,
          ),
        );
      }

      return elements;
    });
  }, [
    group.items,
    group.transitions,
    boundaryPremountFramesByItemId,
    currentFrame,
    defaultPremountFrames,
    width,
    height,
    trackMuted,
    zIndex,
  ]);

  return (
    <Sequence
      from={group.startFrame}
      durationInFrames={group.totalDurationInFrames}
      premountFor={sequencePremountFrames}
    >
      <AbsoluteFill style={{ zIndex }}>
        <TransitionSeries>{transitionElements}</TransitionSeries>
      </AbsoluteFill>
    </Sequence>
  );
};

export const TransitionGroupLayer = memo(TransitionGroupLayerUnmemoized);
